import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  formatInteger,
  IMPORT_MAX_BYTES,
  importFieldLabels,
  importFields,
  importMappingError,
  type ImportField,
  type ImportRowOutcome,
} from '@brimo/shared';
import {
  Banner,
  Button,
  Dialog,
  FileField,
  Pill,
  Select,
  Stepper,
  Table,
  TextField,
  type PillVariant,
} from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { importList, previewImport, useCampaigns, type EventTypeRef, type ImportPreview } from '../api';
import { EventTypeCombobox } from '../EventTypeCombobox';
import { downloadImportErrors } from './importErrors';
import styles from './lists.module.css';

const STEPS = [
  { id: 'file', label: 'Datei' },
  { id: 'columns', label: 'Spalten zuordnen' },
  { id: 'preview', label: 'Vorschau' },
] as const;

const ACCEPT = '.xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv';
const FIELD_OPTIONS = importFields.map((field) => ({ value: field, label: importFieldLabels[field] }));

const OUTCOME_TONES: Record<ImportRowOutcome, PillVariant> = {
  new: 'accent',
  existing: 'info',
  blocked: 'danger',
  invalid_phone: 'warning',
  duplicate_in_file: 'neutral',
};

interface FileErrors {
  file?: string | undefined;
  name?: string | undefined;
  eventType?: string | undefined;
}

/**
 * „Aus Excel importieren" (D-238, no design frame — D-245): three steps „Datei" → „Spalten zuordnen" → „Vorschau".
 * The file is read by the server; the preview counts new contacts, existing contacts, blocked contacts, invalid phone
 * numbers and repeats in the file, shows ten rows and offers the skipped rows as a CSV before „Import starten".
 */
export function ImportListDialog({
  open,
  prefill,
  onClose,
}: {
  open: boolean;
  prefill: { name: string; eventType: EventTypeRef | null };
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const campaigns = useCampaigns(open);
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState(prefill.name);
  const [eventType, setEventType] = useState<EventTypeRef | null>(prefill.eventType);
  const [campaignId, setCampaignId] = useState('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mapping, setMapping] = useState<ImportField[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FileErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(prefill.name);
    setEventType(prefill.eventType);
  }, [open, prefill]);

  function reset() {
    setStep(0);
    setFile(null);
    setCampaignId('');
    setPreview(null);
    setMapping([]);
    setFieldErrors({});
    setBanner(null);
  }

  function close() {
    reset();
    onClose();
  }

  function pickFile(next: File | null) {
    setFile(next);
    setPreview(null);
    setFieldErrors((current) => ({ ...current, file: undefined }));
    if (next !== null && name.trim() === '') setName(next.name.replace(/\.(xlsx|csv)$/i, ''));
    if (next !== null && next.size > IMPORT_MAX_BYTES) {
      setFieldErrors((current) => ({
        ...current,
        file: 'Die Datei ist zu groß. Erlaubt sind bis zu 10 MB.',
      }));
    }
  }

  async function run<T>(action: () => Promise<T>): Promise<T | null> {
    setPending(true);
    setBanner(null);
    try {
      return await action();
    } catch (caught) {
      if (caught instanceof ApiError && caught.fields['file']) {
        setFieldErrors((current) => ({ ...current, file: caught.fields['file'] }));
        setStep(0);
      } else if (caught instanceof ApiError && caught.fields['mapping']) {
        setBanner(caught.fields['mapping'] ?? caught.message);
      } else if (caught instanceof ApiError && Object.keys(caught.fields).length > 0) {
        setFieldErrors((current) => ({
          ...current,
          name: caught.fields['list.name'],
          eventType: caught.fields['list.eventTypeId'] ?? caught.fields['eventTypeId'],
        }));
        setBanner(Object.values(caught.fields)[0] ?? caught.message);
      } else setBanner(errorMessage(caught));
      return null;
    } finally {
      setPending(false);
    }
  }

  async function toColumns() {
    const next: FileErrors = {};
    if (file === null) next.file = 'Bitte eine Datei hochladen.';
    else if (file.size > IMPORT_MAX_BYTES) next.file = 'Die Datei ist zu groß. Erlaubt sind bis zu 10 MB.';
    if (name.trim() === '') next.name = 'Pflichtangabe.';
    if (eventType === null) next.eventType = 'Bitte wählen Sie einen Ereignistyp.';
    setFieldErrors(next);
    if (next.file || next.name || next.eventType || file === null) return;
    const result = await run(() => previewImport(file));
    if (result === null) return;
    setPreview(result);
    setMapping(result.mapping);
    setStep(1);
  }

  async function toPreview() {
    if (file === null) return;
    const error = importMappingError(mapping);
    if (error !== null) {
      setBanner(error);
      return;
    }
    const result = await run(() => previewImport(file, mapping));
    if (result === null) return;
    setPreview(result);
    setStep(2);
  }

  async function start() {
    if (file === null || eventType === null) return;
    const detail = await run(() =>
      importList(file, mapping, {
        name: name.trim(),
        eventTypeId: eventType.id,
        campaignId: campaignId === '' ? null : campaignId,
      }),
    );
    if (detail === null) return;
    await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
    close();
    void navigate(`/dialer/lists/${detail.id}`);
  }

  const counts = step === 2 ? preview?.counts : null;
  const mappingError = step === 1 ? importMappingError(mapping) : null;

  const footer = (
    <>
      {step === 0 ? (
        <Button variant="secondary" onClick={close}>
          Abbrechen
        </Button>
      ) : (
        <Button variant="secondary" disabled={pending} onClick={() => setStep(step - 1)}>
          Zurück
        </Button>
      )}
      {step === 0 ? (
        <Button loading={pending} onClick={() => void toColumns()}>
          Weiter
        </Button>
      ) : step === 1 ? (
        <Button loading={pending} disabled={mappingError !== null} onClick={() => void toPreview()}>
          Weiter
        </Button>
      ) : (
        <Button loading={pending} onClick={() => void start()}>
          Import starten
        </Button>
      )}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={close}
      eyebrow="Kontakte · Listen"
      title="Aus Excel importieren"
      width={760}
      footer={footer}
    >
      <div className={styles.dialogBody}>
        <Stepper orientation="horizontal" current={step} steps={STEPS} label="Schritte des Imports" />
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {step === 0 ? (
          <>
            <FileField
              label="Datei"
              title="Excel- oder CSV-Datei hierher ziehen"
              description="XLSX oder CSV bis 10 MB und 20.000 Zeilen. Die erste Zeile enthält die Spaltennamen."
              buttonLabel="Datei wählen"
              accept={ACCEPT}
              multiple={false}
              onFiles={(files) => pickFile(files[0] ?? null)}
              items={file ? [{ id: 'file', name: file.name }] : []}
              onRemove={() => pickFile(null)}
              error={fieldErrors.file}
            />
            <TextField
              label="Name der Liste"
              required
              value={name}
              error={fieldErrors.name}
              onChange={(event) => setName(event.target.value)}
            />
            <div className={styles.dialogField}>
              <span className={styles.dialogLabel}>
                Ereignistyp für Termine aus dieser Liste<span className={styles.required}> *</span>
              </span>
              <EventTypeCombobox
                contactId={null}
                value={eventType}
                onChange={setEventType}
                label="Ereignistyp für Termine aus dieser Liste"
                invalid={fieldErrors.eventType !== undefined}
              />
              {fieldErrors.eventType ? (
                <span className={styles.dialogError}>{fieldErrors.eventType}</span>
              ) : null}
            </div>
            <Select
              label="Kampagne"
              optional
              value={campaignId}
              onChange={(event) => setCampaignId(event.target.value)}
              options={[
                { value: '', label: 'Keine Kampagne' },
                ...(campaigns.data?.items ?? []).map((item) => ({ value: item.id, label: item.name })),
              ]}
              hint="Für Auswertung und Priorität: Kontakte aus dieser Liste zählen zur gewählten Kampagne."
            />
            <p className={styles.dialogHint}>
              Neue Personen aus der Datei landen nicht in der Kontaktliste. Sie werden erst zu Kontakten, wenn
              ein Setter sie erreicht. Wer nie erreicht wird, wird beim Abschließen der Liste gelöscht.
            </p>
          </>
        ) : null}
        {step === 1 && preview ? (
          <>
            <p className={styles.dialogHint}>
              {`${formatInteger(preview.totalRows)} Zeilen in ${preview.fileName}. Ordnen Sie jeder Spalte ein Feld zu — eine Spalte „Telefon“ ist Pflicht. „Zusatzinfo“ sieht der Setter während des Anrufs.`}
            </p>
            {mappingError ? <Banner tone="warning">{mappingError}</Banner> : null}
            <div className={styles.mappingList}>
              {preview.columns.map((column) => (
                <div key={column.index} className={styles.mappingRow}>
                  <div className={styles.mappingColumn}>
                    <span className={styles.mappingHeader}>{column.header}</span>
                    <span className={styles.mappingSamples}>
                      {column.samples.length > 0 ? column.samples.join(' · ') : 'leer'}
                    </span>
                  </div>
                  <Select
                    label={`Feld für „${column.header}“`}
                    labelHidden
                    size="compact"
                    value={mapping[column.index] ?? 'ignore'}
                    options={FIELD_OPTIONS}
                    onChange={(event) => {
                      const value = event.target.value as ImportField;
                      setMapping((current) =>
                        current.map((field, index) => (index === column.index ? value : field)),
                      );
                    }}
                  />
                </div>
              ))}
            </div>
          </>
        ) : null}
        {step === 2 && preview && counts ? (
          <>
            <div className={styles.importCounts}>
              <ImportCount label="Neu" value={counts.new} hint="werden Importkontakte" />
              <ImportCount label="Bereits Kontakt" value={counts.existing} hint="kommen in die Liste" />
              <ImportCount label="Gesperrt" value={counts.blocked} hint="übersprungen" />
              <ImportCount label="Ungültige Telefonnummer" value={counts.invalidPhone} hint="übersprungen" />
              <ImportCount
                label="Doppelt in der Datei"
                value={counts.duplicateInFile}
                hint="erste Zeile zählt"
              />
            </div>
            {preview.async ? (
              <Banner tone="info">
                Die Datei hat mehr als 2.000 Zeilen. Der Import läuft im Hintergrund, den Fortschritt sehen
                Sie in der Liste.
              </Banner>
            ) : null}
            <Table
              label="Vorschau der ersten zehn Zeilen"
              density="compact"
              rows={preview.sample}
              rowKey={(row) => String(row.rowNumber)}
              columns={[
                { id: 'row', header: 'Zeile', track: '64px', numeric: true, cell: (row) => row.rowNumber },
                { id: 'name', header: 'Name', cell: (row) => row.name },
                { id: 'phone', header: 'Telefon', cell: (row) => row.phone || '—' },
                { id: 'email', header: 'E-Mail', cell: (row) => row.email ?? '—' },
                {
                  id: 'outcome',
                  header: 'Ergebnis',
                  track: '190px',
                  cell: (row) => <Pill variant={OUTCOME_TONES[row.outcome]}>{row.outcomeLabel}</Pill>,
                },
              ]}
            />
            {preview.errors.length > 0 ? (
              <div className={styles.importErrorsLine}>
                <span className={styles.dialogHint}>
                  {`${formatInteger(preview.errors.length)} Zeilen werden übersprungen.`}
                </span>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => downloadImportErrors(preview.fileName, preview.errors)}
                >
                  Fehlerliste herunterladen
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

function ImportCount({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className={styles.importCount}>
      <span className={styles.importCountValue}>{formatInteger(value)}</span>
      <span className={styles.importCountLabel}>{label}</span>
      <span className={styles.importCountHint}>{hint}</span>
    </div>
  );
}
